import { yupResolver } from '@hookform/resolvers/yup'
import { t } from 'commons/libs/i18n'
import { getKeyByTitleOrLabel } from 'commons/utils/array'
import { applyCNPJMask } from 'commons/utils/mask'
import {
  BatchSearch,
  DatePicker,
  DSInputType,
  Flex,
  Icon,
  Input,
  Multiselect,
  SlimButton,
  Tooltip,
} from 'components'
import type { EdiStatus, EdiStatusOption } from 'constants/enums'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import {
  type EdiFilterValues,
  getDefaultEdiFilterFormValues,
  getDefaultEdiFilters,
} from '../../helpers'
import * as S from './styles'
import { filterSchema } from './validations'

interface FilterProps {
  onClear?: () => void
  onSearch: (filters: EdiFilterValues) => void
  statusOptions: EdiStatusOption[]
}

export const Filter = ({ onClear, onSearch, statusOptions }: FilterProps) => {
  const statusTitleOptions = statusOptions.map((item) => item.title)
  const defaultValues = getDefaultEdiFilterFormValues(statusOptions)

  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(filterSchema),
  })

  const { control, handleSubmit, reset } = methods

  const handleSearch = (values: typeof defaultValues) => {
    const data: EdiFilterValues = {
      ...values,
      status: getKeyByTitleOrLabel(values.status, statusOptions) as EdiStatus[],
    }

    onSearch(data)
  }

  const handleClear = () => {
    reset()
    onSearch(getDefaultEdiFilters(statusOptions))
    onClear?.()
  }

  return (
    <FormProvider {...methods}>
      <S.Form onSubmit={handleSubmit(handleSearch)}>
        <S.GridContainer>
          <S.FieldWrapper className="grid-1-4">
            <Controller
              control={control}
              name="startDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  error={fieldState.error?.message}
                  height={30}
                  label={t('common.start')}
                  onChange={(value) => field.onChange(value)}
                  placeholder="dd/mm/aaaa"
                />
              )}
            />
          </S.FieldWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="endDate"
              render={({ field, fieldState }) => (
                <DatePicker
                  {...field}
                  error={fieldState.error?.message}
                  height={30}
                  label={t('common.end')}
                  onChange={(value) => field.onChange(value)}
                  placeholder="dd/mm/aaaa"
                />
              )}
            />
          </S.FieldWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <Multiselect
                  {...field}
                  label={t('common.status')}
                  optionsList={statusTitleOptions}
                  selectedOptions={field.value as unknown as string[]}
                />
              )}
            />
          </S.FieldWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="customerOrder"
              render={({ field }) => (
                <BatchSearch
                  {...field}
                  label={t('screens.edi.filter.customerOrder')}
                  onClear={() => field.onChange('')}
                  placeHolder={t('common.placeholders.clickToWrite')}
                />
              )}
            />
          </S.FieldWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="legalName"
              render={({ field }) => (
                <BatchSearch
                  {...field}
                  label={t('common.legalName')}
                  onClear={() => field.onChange('')}
                  placeHolder={t('common.placeholders.clickToWrite')}
                />
              )}
            />
          </S.FieldWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="sapCode"
              render={({ field, fieldState }) => (
                <BatchSearch
                  {...field}
                  error={fieldState.error?.message}
                  label={t('common.sapCode')}
                  onClear={() => field.onChange('')}
                  placeHolder="ˍ ˍ ˍ ˍ ˍ ˍ ˍ"
                />
              )}
            />
          </S.FieldWrapper>
          <S.FieldWrapper>
            <Controller
              control={control}
              name="cnpj"
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  design={DSInputType.SEARCH_INPUT}
                  error={fieldState.error?.message}
                  id="cnpj"
                  label={t('common.cnpj')}
                  maxLength={18}
                  // TODO: Refactor applyCNPJMask to accept new CNPJ Alphanumeric Pattern
                  onChange={(e) => field.onChange(applyCNPJMask(e.target.value))}
                  placeholder="__.___.___/____-__"
                />
              )}
            />
          </S.FieldWrapper>
          <S.ButtonWrapper>
            <SlimButton content={t('buttons.search')} type="submit" variant="primary" />
          </S.ButtonWrapper>
          <S.ButtonWrapper>
            <Tooltip arrow title={t('buttons.clear')}>
              <Flex fullWidth>
                <SlimButton
                  icon={<Icon icon="close" size="20" />}
                  onClick={handleClear}
                  type="button"
                  variant="border"
                />
              </Flex>
            </Tooltip>
          </S.ButtonWrapper>
        </S.GridContainer>
      </S.Form>
    </FormProvider>
  )
}
